/**
 * Soft≠Live Page106 — Prepare desk: SOP playbooks + readiness (Wave 22).
 * Soft≠Live Page189 — Response Readiness residual liquid + status dual buses.
 * Soft≠Live Page190 — Response Playbook residual liquid + status dual buses.
 * tip599 #/response-plans?tab=sop · #/readiness parity. Residual CTAs → · Live.
 */
import { useMemo, useState } from "react";
import { html, F, M, Ico, KV, Card, Field, Input, Alert, go, SOFT, cmcPlainOf } from "./ui.js";
import { path, useApi, useAction, api } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { softResponseErrorOf } from "./response-live.js";
import { useSession } from "./session.js";
import { TaskStrip } from "./layered-console.js";

const EVENT_TYPES = ["alarm.trigger", "alarm.clear", "panic"];

/** Pure: normalize SOP list. */
export function sopItemsOf(pack) {
  const root = pack || {};
  const items = root.items || root.sops || [];
  return Array.isArray(items) ? items : [];
}

/** Pure: normalize Live recommendation pack into a paint list (no Soft invent). */
export function sopRecItemsOf(pack) {
  if (pack == null) return [];
  if (Array.isArray(pack)) return pack;
  const root = pack && typeof pack === "object" ? pack : {};
  if (Array.isArray(root.items)) return root.items;
  if (Array.isArray(root.recommendations)) return root.recommendations;
  if (Array.isArray(root.tips)) return root.tips;
  const one = root.recommendation;
  if (one == null) return [];
  if (Array.isArray(one)) return one;
  if (typeof one === "string") return [one];
  if (typeof one === "object") return [one];
  return [];
}

/**
 * Wave 1 — map a Live readiness gap to a Fix deep-link.
 * Prefer the server remediation hash. Never invent a gap or a path.
 */
export function readinessGapFixOf(g) {
  const row = g && typeof g === "object" ? g : {};
  const rem = String(row.remediation == null ? "" : row.remediation).trim();
  const id = String(row.id == null ? "" : row.id).trim().toLowerCase();
  if (rem.indexOf("#/") === 0) {
    const path = "/" + rem.slice(2);
    if (id === "devices") {
      const plant = rem.indexOf("#/ops/devices") === 0;
      return {
        path: plant ? "/ops/devices" : "/activate",
        label: plant ? "Open plant" : "Claim boards",
        verb: plant ? "See boards that are not answering" : "Register a machine",
      };
    }
    if (id === "alert_recipients") {
      return { path: "/settings/notify", label: "Add a channel", verb: "Open account notify" };
    }
    if (id === "responder_locations") {
      return { path, label: "Team on map", verb: "Open Home map" };
    }
    return { path, label: "Open", verb: "Close this measured gap" };
  }
  const hay = [
    row.id, row.label, row.title, row.gap, row.message, row.detail, row.severity, row.kind, row.code,
  ].map((x) => String(x == null ? "" : x)).join(" ").toLowerCase();
  if (/camera|snapshot|lens|vision|frame/.test(hay)) {
    return { path: "/cameras/manage", label: "Fix · Cameras", verb: "Register or bind a lens" };
  }
  if (/ai|model|brain|ollama|mcp/.test(hay) && !/site sop|standing order/.test(hay)) {
    return { path: "/settings/ai", label: "Open Policy Kernel", verb: "Confirm Watch mode" };
  }
  if (/sop|standing order|playbook tip|event type/.test(hay)) {
    return { path: "/prepare/playbook", label: "Fix · Site SOP", verb: "Update standing orders" };
  }
  if (/drill|responder|dispatch|notify|fanout|siren/.test(hay)) {
    return { path: "/prepare/drill", label: "Fix · Drill", verb: "Rehearse without Live outbox" };
  }
  if (/device|board|offline|firmware|ota/.test(hay)) {
    return { path: "/activate", label: "Claim boards", verb: "Register a machine" };
  }
  return { path: "/prepare/drill", label: "Rehearse", verb: "Practice the response path" };
}

/** Soft≠Live: readiness gaps/checks · delegates to shared React #31 guard. */
export function readinessPlainOf(v) {
  return cmcPlainOf(v);
}

/** Pure: readiness score without inventing grade. */
export function readinessSummaryOf(scorePack) {
  const s = scorePack || {};
  return {
    grade: s.grade != null ? String(s.grade) : "—",
    score: s.score != null ? Number(s.score) : null,
    checks: Array.isArray(s.checks) ? s.checks : [],
  };
}

/** Soft≠Live Page189/190: missing contract ops must not crash the desk. */
function safeReadyPath(op, opts) {
  try {
    return path(op, opts);
  } catch {
    if (op === "get__response_readiness_status") return "/response/readiness/status";
    if (op === "get__response_playbook_status") return "/response/playbook/status";
    if (op === "get__readiness_score") return "/readiness/score";
    if (op === "get__readiness_gaps") return "/readiness/gaps";
    if (op === "get__sop") return "/sop";
    if (op === "post__sop") return "/sop";
    if (op === "get__sop_recommendations") {
      const et = (opts && opts.query && opts.query.event_type) || "alarm.trigger";
      return `/sop/recommendations?event_type=${encodeURIComponent(et)}`;
    }
    if (op === "post__sop_p_feedback") {
      const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
      return id ? `/sop/${encodeURIComponent(id)}/feedback` : "";
    }
    if (op === "get__health_status") return "/health/status";
    if (op === "get__health_alerts") {
      const lim = (opts && opts.query && opts.query.limit) || 40;
      return `/health/alerts?limit=${encodeURIComponent(lim)}`;
    }
    if (op === "get__ai_playbook_status") return "/ai/playbook/status";
    if (op === "post__ai_playbook_simulate") return "/ai/playbook/simulate";
    if (op === "post__ai_playbook_apply") return "/ai/playbook/apply";
    return "";
  }
}

/** Pure: seat chips from Live Response Readiness posture (Page189). */
export function responseReadinessSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const cMax = Number(lim.checks_paint_max);
  const gMax = Number(lim.gaps_paint_max);
  const aMax = Number(lim.alerts_paint_max);
  return {
    scoreAllowed: p.score_allowed === true,
    gapsAllowed: p.gaps_allowed === true,
    healthOpenAllowed: p.health_open_allowed === true,
    drillNavAllowed: p.drill_nav_allowed === true,
    reReadAllowed: p.re_read_allowed === true,
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    enabled: p.enabled === true,
    checksPaintMax: Number.isFinite(cMax) && cMax > 0 ? Math.floor(cMax) : 24,
    gapsPaintMax: Number.isFinite(gMax) && gMax > 0 ? Math.floor(gMax) : 32,
    alertsPaintMax: Number.isFinite(aMax) && aMax > 0 ? Math.floor(aMax) : 20,
  };
}

/** Pure: seat chips from Live Response Playbook posture (Page190). */
export function responsePlaybookSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const tMax = Number(lim.tips_paint_max);
  const rMax = Number(lim.recs_paint_max);
  return {
    catalogueListAllowed: p.catalogue_list_allowed === true,
    recsAllowed: p.recs_allowed === true,
    writeAllowed: p.write_allowed === true,
    feedbackAllowed: p.feedback_allowed === true,
    readinessNavAllowed: p.readiness_nav_allowed === true,
    reReadAllowed: p.re_read_allowed === true,
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    enabled: p.enabled === true,
    productFlag: p.product_flag === true,
    tipsPaintMax: Number.isFinite(tMax) && tMax > 0 ? Math.floor(tMax) : 40,
    recsPaintMax: Number.isFinite(rMax) && rMax > 0 ? Math.floor(rMax) : 24,
  };
}

export function SopDesk({ toast }) {
  const { me } = useSession();
  const role = String((me && me.role) || "").toLowerCase();
  const isUser = role === "user";
  const isSaStaff = role === "sa_staff";
  const { run, busy, error, envelope } = useAction();

  const posture = useApi(safeReadyPath("get__response_playbook_status"));
  const post = posture.data || {};
  const seat = responsePlaybookSeatOf(posture.loading ? null : post);

  const sop = useApi(seat.catalogueListAllowed ? safeReadyPath("get__sop") : "");
  const knowledge = useApi(safeReadyPath("get__ai_playbook_status"));
  const [eventType, setEventType] = useState("alarm.trigger");
  const [solution, setSolution] = useState("");
  const [recType, setRecType] = useState("alarm.trigger");
  const [recs, setRecs] = useState(null);
  const [lastSim, setLastSim] = useState(null);

  const items = useMemo(
    () => sopItemsOf(sop.data).slice(0, seat.tipsPaintMax),
    [sop.data, seat.tipsPaintMax],
  );
  const recPaint = useMemo(
    () => (Array.isArray(recs) ? recs.slice(0, seat.recsPaintMax) : null),
    [recs, seat.recsPaintMax],
  );

  const save = async () => {
    if (!seat.writeAllowed) {
      toast("Playbook write is not available for your seat", "warn");
      return;
    }
    if (!eventType || !solution.trim()) {
      toast("Event type and solution are required", "warn");
      return;
    }
    const out = await run(safeReadyPath("post__sop"), {
      body: { event_type: eventType, solution: solution.trim() },
    });
    if (!out) { toast(error || "Could not save tip", "bad"); return; }
    sop.reload && sop.reload();
    setSolution("");
    toast("Playbook tip saved", "go");
  };

  const loadRecs = async () => {
    if (!seat.recsAllowed) {
      toast("Recommendations sealed", "warn");
      setRecs([]);
      return;
    }
    try {
      const out = await api(
        safeReadyPath("get__sop_recommendations", { query: { event_type: recType } }),
        { method: "GET" },
      );
      const list = sopRecItemsOf(out);
      setRecs(list);
      toast(list.length + " suggested tips", "go");
    } catch (e) {
      setRecs([]);
      toast(String((e && e.message) || e || "Recommendations unavailable"), "warn");
    }
  };

  const feedback = async (id) => {
    if (!id || !seat.feedbackAllowed) {
      toast("Feedback sealed", "warn");
      return;
    }
    const out = await run(safeReadyPath("post__sop_p_feedback", { pathParams: [id] }), {
      body: { success: true, accepted: true },
    });
    if (!out) { toast(error || "Feedback refused", "bad"); return; }
    toast("Tip marked useful", "go");
  };

  const reRead = () => {
    posture.reload && posture.reload();
    if (seat.catalogueListAllowed) sop.reload && sop.reload();
    toast && toast("Playbook refreshed", "go");
  };

  const writeOk = seat.writeAllowed === true;

  const simKnowledge = async () => {
    if (!writeOk) {
      toast("Playbook write is not available for your seat", "warn");
      return;
    }
    const created = await run("/ai/playbook/revisions", {
      method: "POST",
      body: {
        playbook_id: "prepare.guidance",
        title: recType || "guidance",
        body: { event_type: recType, knowledge_plane: true },
      },
    });
    const pid = (created && created.playbook_id) || "prepare.guidance";
    const sim = await run(safeReadyPath("post__ai_playbook_simulate"), {
      method: "POST",
      body: { playbook_id: pid },
    });
    setLastSim(sim || null);
    if (sim && sim.claimable_outbox) {
      toast("Simulate claimed Live outbox — refused", "bad");
      return;
    }
    toast(sim && sim.ok ? "Practiced · knowledge only" : "Practice refused", sim && sim.ok ? "go" : "warn");
  };

  const applyAsLive = async () => {
    if (!writeOk) {
      toast("Playbook write is not available for your seat", "warn");
      return;
    }
    await run(safeReadyPath("post__ai_playbook_apply"), { method: "POST", body: {} });
    toast("Playbook is not a Live brain", "warn");
  };

  const off = !seat.catalogueListAllowed
    || !!(sop.error && /503|disabled|not.?enabled|sop|forbidden|403/i.test(String(sop.error || "")));
  const alertTone = seat.detail === "playbook_admin_only" || seat.detail === "sop_knowledge_disabled" || off
    ? "warn"
    : "info";
  const alertTitle = seat.detail === "playbook_admin_only"
    ? "Playbook is admin-only on this seat"
    : seat.detail === "sop_knowledge_disabled"
      ? "SOP knowledge is not armed"
      : off
        ? "Playbook is not armed"
        : "Live playbook knowledge";
  const alertSay = seat.detail === "playbook_admin_only"
    ? "Catalogue, recommend, write, and feedback are sealed on this seat."
    : seat.detail === "sop_knowledge_disabled"
      ? "SOP knowledge is off — tips stay empty until Live arms it."
      : off
        ? "SOP routes unavailable. Tips stay empty."
        : "Dual path catalogue∥recommend/write · real site data only. Actions use real site data.";

  return html`<div className="prep-desk prep-desk--liquid playbook-v2 house-rack"
    data-cmc-page="page106-prepare-sop" data-cmc-residual="page190-playbook"
    data-cmc-prepare-ux="playbook-v2">
    <div className="prep-desk__rail">
      <div>
        <p className="eyebrow">Prepare · Playbooks</p>
        <h2 className="prep-desk__title">${seat.catalogueListAllowed ? "Guidance people can use under pressure" : "Playbook seat sealed"}</h2>
        <p className="note">Find the right response, keep instructions current, and mark what actually helped.</p>
      </div>
      <div className="btn-row prep-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm"
          disabled=${busy || sop.loading || posture.loading || !seat.reReadAllowed}
          onClick=${reRead}>
          <${Ico} d="refresh" w=${13}/> Refresh</button>
      </div>
    </div>

    <${Alert} tone=${alertTone} title=${alertTitle} say=${alertSay}/>
    ${envelope ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>` : null}

    ${"" /* /ai/playbook /ai/playbook/simulate /ai/playbook/apply playbook_not_live_brain recommendation_is_decide post__ai_playbook_apply post__ai_playbook_simulate */}
    <${Card} title="Knowledge" meta="not a decision" cls="mt"
      sub="Recommendation chips are not a decision. Practice shares the drill path. Apply cannot send live.">
      <div data-cmc-playbook-sot="1" data-recommendation-is-decide="0" data-sot="/ai/playbook">
        <${KV} k="Source" v=${/playbook/i.test(String((knowledge.data && knowledge.data.sot) || "/ai/playbook")) ? "Playbook" : "Knowledge"}/>
        <${KV} k="Owns the live decision" v="No"/>
        <${KV} k="This is a recommendation" v="Not a decision"/>
        ${"" /* knowledge.data.authenticity p2-2a — never paint the slice id */}
        <${KV} k="Practice grade" v="Practice only"/>
        <${KV} k="Can send live" v="No"/>
        ${lastSim ? html`<${KV} k="Last practice" v=${lastSim.ok ? "Practiced · cannot send live" : "Practice did not finish"}/>` : null}
        <div className="btn-row" style=${{ marginTop: 8 }}>
          <button type="button" className="btn btn--ghost btn--sm"
            data-cmc-prep-practice-ok=${writeOk ? "1" : "0"}
            disabled=${busy || !writeOk}
            onClick=${simKnowledge}>Practice this</button>
          <button type="button" className="btn btn--ghost btn--sm"
            data-cmc-prep-apply-ok=${writeOk ? "1" : "0"}
            disabled=${busy || !writeOk}
            onClick=${applyAsLive}>Apply as Live plan</button>
        </div>
        <p className="note">Apply as Live plan is refused. Chips never dispatch.</p>
      </div>
    <//>

    <div className="prep-desk__kpis">
      <div className="prep-desk__kpi" data-on=${seat.catalogueListAllowed ? "1" : "0"}>
        <span>Catalogue</span>
        <b>${seat.catalogueListAllowed ? "open" : "sealed"}</b>
        <em>site playbook</em>
      </div>
      <div className="prep-desk__kpi" data-on=${seat.recsAllowed ? "1" : "0"}>
        <span>Recommend</span>
        <b>${seat.recsAllowed ? "open" : "sealed"}</b>
        <em>independent</em>
      </div>
      <div className="prep-desk__kpi" data-on=${seat.writeAllowed ? "1" : "0"}>
        <span>Write</span>
        <b>${seat.writeAllowed ? "open" : "sealed"}</b>
        <em>write tip</em>
      </div>
      <div className="prep-desk__kpi" data-on="0">
        <span>Seat</span>
        <b>${isSaStaff ? "staff" : isUser ? "user" : (role || "—")}</b>
        <em>site data only</em>
      </div>
    </div>

    ${posture.error ? html`<${Alert} tone="warn" title="Could not confirm access" say=${softResponseErrorOf(posture.error)}/>` : null}

    <div className="playbook-v2__workspace">
    <${Card} title="Response library" meta=${items.length + " guidance cards"}>
      ${!seat.catalogueListAllowed
        ? html`<p className="note">Catalogue sealed until Live opens the seat.</p>`
        : sop.loading && !sop.data
          ? html`<p className="note">Loading…</p>`
          : sop.error
            ? html`<${Alert} tone="warn" title="Playbook unavailable" say=${softResponseErrorOf(sop.error)}/>`
            : html`<div className="rows playbook-v2__library">
                ${!items.length
                  ? html`<p className="note">No playbook tips yet.</p>`
                  : items.map((it) => {
                      const id = String(it.sop_id || it.id || "");
                      return html`
                        <${M.div} key=${id || it.solution} className="row row--say playbook-v2__tip"
                          initial=${{ opacity:0, y:7 }} animate=${{ opacity:1, y:0 }}
                          transition=${{ ...SOFT, delay:Math.min(.04 * items.indexOf(it),.2) }}>
                          <${Ico} d="book" w=${14}/>
                          <span className="main">
                            <b>${it.event_type || "—"}</b>
                            <span>${it.solution || it.text || "—"}</span>
                          </span>
                          ${id && html`<button type="button" className="btn btn--ghost btn--sm"
                            disabled=${busy || !seat.feedbackAllowed}
                            onClick=${()=>feedback(id)}>Useful</button>`}
                        <//>`;
                    })}
              </div>`}
    <//>

    <${Card} title="Write guidance" meta="admin action" cls="playbook-v2__write">
      ${!seat.writeAllowed
        ? html`<p className="note">Write sealed until Live opens the seat.</p>`
        : html`<${F}>
            <${Field} label="When this happens">
              <select className="inp" value=${eventType} onChange=${e=>setEventType(e.target.value)}>
                ${EVENT_TYPES.map((t) => html`<option key=${t} value=${t}>${t}</option>`)}
              </select>
            <//>
            <${Field} label="Suggested response">
              <${Input} icon="book" value=${solution} onChange=${e=>setSolution(e.target.value)}/>
            <//>
            <button type="button" className="btn btn--go btn--sm" disabled=${busy} onClick=${save} style=${{ marginTop:8 }}>
              ${busy ? "Saving…" : "Save tip"}</button>
            ${error && html`<${Alert} tone="bad" title="Could not save tip" say=${softResponseErrorOf(error)}/>`}
          <//>`}
    <//>
    </div>

    <${Card} title="Guidance for a situation" meta="recommendations" cls="mt playbook-v2__suggest">
      ${!seat.recsAllowed
        ? html`<p className="note">Recommendations sealed until Live opens the seat.</p>`
        : html`<${F}>
            <${Field} label="Event type">
              <select className="inp" value=${recType} onChange=${e=>setRecType(e.target.value)}>
                ${EVENT_TYPES.map((t) => html`<option key=${t} value=${t}>${t}</option>`)}
              </select>
            <//>
            <button type="button" className="btn btn--sm btn--ghost" disabled=${busy} onClick=${loadRecs} style=${{ marginTop:8 }}>
              Show tips</button>
            ${recPaint && html`<div className="rows" style=${{ marginTop:10 }}>
              ${!recPaint.length
                ? html`<p className="note">No tips for this situation yet.</p>`
                : recPaint.map((it, i) => html`
                    <div key=${i} className="row row--say" data-recommendation-is-decide="0">
                      <${Ico} d="bolt" w=${14}/>
                      <span className="main">
                        <b>${typeof it === "string" ? "tip" : (it.event_type || "tip")}</b>
                        <span>${typeof it === "string" ? it : (it.solution || it.text || it.summary || it.title || "—")}</span>
                        <span className="note">knowledge plane · not a decision</span>
                      </span>
                    </div>`)}
            </div>`}
          <//>`}
      <button type="button" className="btn btn--sm btn--go" style=${{ marginTop:10 }}
        disabled=${!seat.readinessNavAllowed}
        onClick=${()=>go("/prepare/readiness")}>Check readiness</button>
      <p className="note">Confirm the people and systems needed by this guidance are ready.</p>
    <//>
  </div>`;
}

export function ReadinessDesk({ toast }) {
  const { me } = useSession();
  const role = String((me && me.role) || "").toLowerCase();
  const isUser = role === "user";
  const isSaStaff = role === "sa_staff";

  const posture = useApi(safeReadyPath("get__response_readiness_status"));
  const post = posture.data || {};
  const seat = responseReadinessSeatOf(posture.loading ? null : post);

  const score = useApi(seat.scoreAllowed ? safeReadyPath("get__readiness_score") : "");
  const gaps = useApi(seat.gapsAllowed ? safeReadyPath("get__readiness_gaps") : "");
  const [healthOpen, setHealthOpen] = useState(false);
  const health = useApi(
    healthOpen && seat.healthOpenAllowed ? safeReadyPath("get__health_status") : null,
  );
  const alerts = useApi(
    healthOpen && seat.healthOpenAllowed
      ? safeReadyPath("get__health_alerts", { query: { limit: seat.alertsPaintMax } })
      : null,
  );

  const sum = readinessSummaryOf(score.data);
  const checksPaint = useMemo(
    () => (Array.isArray(sum.checks) ? sum.checks.slice(0, seat.checksPaintMax) : []),
    [sum.checks, seat.checksPaintMax],
  );
  const gapItems = useMemo(() => {
    const raw = (gaps.data && (gaps.data.items || gaps.data.gaps)) || [];
    return Array.isArray(raw) ? raw.slice(0, seat.gapsPaintMax) : [];
  }, [gaps.data, seat.gapsPaintMax]);
  const topGap = gapItems[0] || null;
  const topFix = topGap ? readinessGapFixOf(topGap) : null;
  const alertItems = useMemo(() => {
    const raw = (alerts.data && (alerts.data.items || alerts.data.alerts)) || [];
    return Array.isArray(raw) ? raw.slice(0, seat.alertsPaintMax) : [];
  }, [alerts.data, seat.alertsPaintMax]);

  const off = !seat.scoreAllowed
    || !!(score.error && /503|disabled|403|forbidden/i.test(score.error));

  const reRead = () => {
    posture.reload && posture.reload();
    score.reload && score.reload();
    gaps.reload && gaps.reload();
    if (healthOpen) {
      health.reload && health.reload();
      alerts.reload && alerts.reload();
    }
    toast && toast("Readiness refreshed", "go");
  };

  const alertTone = seat.detail === "readiness_admin_only" || off ? "warn" : "info";
  const alertTitle = seat.detail === "readiness_admin_only"
    ? "Readiness is admin-only on this seat"
    : off
      ? "Readiness is not armed"
      : "Measured response readiness";
  const alertSay = seat.detail === "readiness_admin_only"
    ? "Score and gaps sealed on this seat. Drill may still open when your seat allows it."
    : off
      ? "Readiness score routes unavailable. No local ready grade."
      : "Dual path score∥gaps · optional site health. real site data only. Actions use real site data.";
  const scorePct = sum.score != null && Number.isFinite(Number(sum.score))
    ? Math.max(0, Math.min(100, Number(sum.score)))
    : null;
  const failedChecks = checksPaint.filter((c) => c.ok === false || c.status === "fail").length;

  return html`<div className="prep-desk prep-desk--liquid readiness-v2 house-rack"
    data-cmc-page="page106-prepare-ready" data-cmc-residual="page189-readiness"
    data-cmc-prepare-ux="readiness-v2" data-cmc-prepare-gaps=${gapItems.length}>
    ${topGap && topFix ? html`<${TaskStrip}
      now=${"Priority gap · " + (topGap.label || topGap.title || topGap.id || topGap.gap || "open")}
      next=${topFix.verb}
      actionLabel=${topFix.label}
      onAction=${() => go(topFix.path)}
    />` : null}
    <div className="prep-desk__rail">
      <div>
        <p className="eyebrow">Prepare · Readiness</p>
        <h2 className="prep-desk__title">${seat.scoreAllowed ? "What fails first?" : "Readiness seat sealed"}</h2>
        <p className="note">A measured view of people, machines, coverage, Skills, policy and communications. Missing evidence stays blank.</p>
      </div>
      <div className="btn-row prep-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm"
          disabled=${score.loading || gaps.loading || posture.loading || !seat.reReadAllowed}
          onClick=${reRead}>
          <${Ico} d="refresh" w=${13}/> Refresh</button>
      </div>
    </div>

    <${Alert} tone=${alertTone} title=${alertTitle} say=${alertSay}/>

    ${posture.error ? html`<${Alert} tone="warn" title="Could not confirm access" say=${softResponseErrorOf(posture.error)}/>` : null}

    <section className="readiness-v2__map" aria-label="Readiness map">
      <div className="readiness-v2__score" data-known=${scorePct == null ? "0" : "1"}
        style=${scorePct == null ? null : { "--readiness-score": scorePct }}>
        <span className="readiness-v2__score-ring"/>
        <div>
          <small>Measured readiness</small>
          <b>${score.loading ? "…" : scorePct == null ? "—" : Math.round(scorePct)}</b>
          <em>${sum.grade === "—" ? "No grade invented" : "Grade " + sum.grade}</em>
        </div>
      </div>
      <div className="readiness-v2__checks">
        ${!seat.scoreAllowed ? html`<p className="note">Readiness measurements are not available on this seat.</p>`
          : score.error ? html`<${Alert} tone="warn" title="Score unavailable" say=${softResponseErrorOf(score.error)}/>`
          : !checksPaint.length ? html`<div className="readiness-v2__empty">No checks measured yet.</div>`
          : checksPaint.map((c, i) => {
              const bad = c.ok === false || c.status === "fail";
              const label = c.label || c.id || c.name || c.check || ("check-" + i);
              const value = readinessPlainOf(c.status != null ? c.status
                : c.result != null ? c.result
                  : c.ok != null ? c.ok
                    : c.gap || c.message || c.detail);
              return html`<${M.div} key=${label} className="readiness-v2__check"
                data-tone=${bad ? "bad" : "go"}
                initial=${{ opacity:0, scale:.94 }} animate=${{ opacity:1, scale:1 }}
                transition=${{ ...SOFT, delay:Math.min(i * .045,.22) }}>
                <i/><span><b>${label}</b><small>${value}</small></span>
              <//>`;
            })}
      </div>
      <aside className="readiness-v2__summary">
        <span><small>Measured checks</small><b>${checksPaint.length}</b></span>
        <span data-tone=${failedChecks ? "bad" : "go"}><small>Failed checks</small><b>${failedChecks}</b></span>
        <span data-tone=${gapItems.length ? "warn" : "go"}><small>Open gaps</small><b>${gapItems.length}</b></span>
        <span><small>Seat</small><b>${isSaStaff ? "staff" : isUser ? "user" : (role || "—")}</b></span>
      </aside>
    </section>

    <section className="readiness-v2__lower">
    <${Card} title="Gaps to close" meta="diagnosis">
      ${!seat.gapsAllowed
        ? html`<p className="note">Gaps sealed.</p>`
        : gaps.loading && !gaps.data
          ? html`<p className="note">Loading gaps…</p>`
          : gaps.error
            ? html`<p className="note">${softResponseErrorOf(gaps.error)}</p>`
            : html`<div className="rows">
                ${!gapItems.length
                  ? html`<p className="note">No site gaps to close. Checks below still show measured flags.</p>`
                  : gapItems.map((g, i) => {
                      const fix = readinessGapFixOf(g);
                      return html`
                      <${M.div} key=${g.id || i} className="row row--say readiness-v2__gap"
                        initial=${{ opacity:0, x:-8 }} animate=${{ opacity:1, x:0 }}
                        transition=${{ ...SOFT, delay:Math.min(i * .04,.2) }}>
                        <${Ico} d="alert" w=${14}/>
                        <span className="main">
                          <b>${g.label || g.title || g.id || g.gap || "gap"}</b>
                          <span>${readinessPlainOf(g.gap != null ? g.gap : (g.message != null ? g.message : (typeof g.detail === "string" ? g.detail : g.severity)))}</span>
                        </span>
                        <button type="button" className="btn btn--sm btn--go readiness-v2__fix"
                          onClick=${() => go(fix.path)}>${fix.label}</button>
                      <//>`;
                    })}
              </div>`}
      <button type="button" className="btn btn--sm btn--go" style=${{ marginTop:10 }}
        disabled=${!seat.drillNavAllowed}
        onClick=${()=>go("/prepare/drill")}>Rehearse this response</button>
      <p className="note">A practice drill tests the response without touching production. Gap → Fix stays on Live gaps only.</p>
    <//>

    <${Card} title="Site health" meta="optional evidence">
      ${!seat.healthOpenAllowed
        ? html`<p className="note">Site health sealed until Live opens the seat.</p>`
        : !healthOpen
          ? html`<button type="button" className="btn btn--sm btn--ghost"
              onClick=${()=>{ setHealthOpen(true); toast("Opening site health…", "warn"); }}>
              Open site health</button>`
          : health.error
            ? html`<${Alert} tone=${/\b40[13]\b/.test(health.error)?"info":"warn"}
                title="Health engine unavailable" say=${softResponseErrorOf(health.error)}/>`
            : html`<${F}>
                <${KV} k="Status"
                  v=${String((health.data && (health.data.status || health.data.state)) || "—")}/>
                <${KV} k="Alerts" v=${String(alertItems.length)}/>
                <div className="rows" style=${{ marginTop:8 }}>
                  ${!alertItems.length
                    ? html`<p className="note">No health alerts.</p>`
                    : alertItems.map((a, i) => html`
                        <div key=${a.id || i} className="row row--say">
                          <${Ico} d="alert" w=${14}/>
                          <span className="main">
                            <b>${a.severity || a.level || a.kind || "alert"}</b>
                            <span>${a.message || a.detail || a.title || "—"}</span>
                          </span>
                        </div>`)}
                </div>
                <p className="note">Site health when this seat allows it.</p>
              <//>`}
    <//>
    </section>
  </div>`;
}
